import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SourceDataObject, VcsBranch, VcsRepository, VcsSource } from 'src/app/core/model/tenants/tenant.model';
import { TenantClientService } from 'src/app/core/services/tenant-client.service';

@Component({
    selector: 'app-create',
    templateUrl: './create.component.html',
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateComponent implements OnInit {
  vcsSources: VcsSource[] = [];
  repositories: VcsRepository[] = [];
  branches: VcsBranch[] = [];

  selectedSource: VcsSource | null = null;
  selectedRepository: VcsRepository | null = null;
  selectedBranch: VcsBranch | null = null;
  sourceDataObject: string | null = null;

  descriptionValue = '';

  constructor(
    private api: TenantClientService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.api.getVcsSources().subscribe(sources => {
      this.vcsSources = sources;
    });
  }

  onSourceChange(source: VcsSource): void {
    this.selectedSource = source;
    this.repositories = [];
    this.branches = [];
    this.selectedRepository = null;
    this.selectedBranch = null;
    this.sourceDataObject = null;

    if (source.gitSource === null) {
      this.fetchSourceDataObject();
      return;
    }

    this.api.getRepositories(source.gitSource).subscribe(repos => {
      this.repositories = repos;
      if (repos.length === 0) {
        this.fetchSourceDataObject();
      }
    });
  }

  onRepositoryChange(repo: VcsRepository): void {
    this.selectedRepository = repo;
    this.branches = [];
    this.selectedBranch = null;
    this.sourceDataObject = null;

    this.api.getBranches(repo.gitSource, repo.gitRepository).subscribe(branches => {
      this.branches = branches;
      if (branches.length === 0) {
        this.fetchSourceDataObject();
      }
    });
  }

  onBranchChange(branch: VcsBranch): void {
    this.selectedBranch = branch;
    this.fetchSourceDataObject();
  }

  private fetchSourceDataObject(): void {
    this.api.getDefaultSourceDataObject(
      this.selectedSource?.gitSource ?? undefined,
      this.selectedRepository?.gitRepository ?? undefined,
      this.selectedBranch?.branch ?? undefined
    ).subscribe((result: SourceDataObject) => {
      this.sourceDataObject = result.sourceDataObject;
    });
  }

  get submitDisabled(): boolean {
    if (!this.selectedSource) return true;
    if (this.repositories.length > 0 && !this.selectedRepository) return true;
    if (this.branches.length > 0 && !this.selectedBranch) return true;
    return false;
  }

  onSubmit(form: NgForm): void {
    if (!form.valid || this.submitDisabled) return;

    const value = {
      ...form.value,
      description: this.descriptionValue,
      gitSource: this.selectedSource?.gitSource ?? null,
      gitRepository: this.selectedRepository?.gitRepository ?? null,
      gitBranch: this.selectedBranch?.branch ?? null,
      sourceDataObject: this.sourceDataObject
    };

    this.api.createTenant(value).subscribe(location => this.initializeTenant(location));
  }

  initializeTenant(location: string): void {
    const segments = location.split('/').filter(Boolean);
    const tenantId = segments[segments.length - 1];
    if (!tenantId) return;
    this.api.loadAssets(tenantId).subscribe(() => this.router.navigate(['/', tenantId]));
  }

  onCancel(): void {
    this.router.navigate(['..'], { relativeTo: this.route });
  }
}
